/**
 * RetentionHoldsTab — Wave 6.3 list + add/remove retention holds.
 *
 * Shows all carts with active retention holds. SuperAdmin can add a hold
 * (cart picker + extended-to-date + reason) or release one (logged in
 * PurgeHistory implicitly by the backend).
 *
 * PILOT-GATE — during pilot phase the list is filtered to the SuperAdmin's
 * branch unless they have explicit cross-branch authority. Backend
 * enforces the same gate; this UI just keeps clutter low.
 */

import { useEffect, useState } from 'react';
import { ApiError } from '@retail-ops/api-client';
import { Banner, Button, ModalShell, Pill } from '@/components/primitives';
import { promptBypassKey } from '@/modules/Admin/editors/_shared/CrudTable';
import { useRecordsApi } from '../hooks/useRecordsApi';
import type { Iso8601, RetentionHold } from '@retail-ops/shared-types';

export function RetentionHoldsTab(): JSX.Element {
  const { client, makeSignal } = useRecordsApi();
  const [rows, setRows] = useState<ReadonlyArray<RetentionHold>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState<boolean>(false);

  async function load(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const res = await client.retail.superAdmin.records.listRetentionHolds({ signal: makeSignal() });
      const anyRes = res as unknown as { success?: boolean; data?: RetentionHold[]; error?: string };
      if (anyRes.success === false) {
        setError(anyRes.error ?? 'Failed to load retention holds.');
        return;
      }
      setRows(anyRes.data ?? []);
    } catch (e: unknown) {
      if (e instanceof Error && e.name === 'AbortError') return;
      setError(e instanceof Error ? e.message : 'Failed to load retention holds.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function release(hold: RetentionHold): Promise<void> {
    const bypass = promptBypassKey();
    if (!bypass) return;
    try {
      const res = await client.retail.superAdmin.records.removeRetentionHold(bypass, hold.cartId, {
        signal: makeSignal(),
      });
      const anyRes = res as unknown as { success?: boolean; error?: string };
      if (anyRes.success === false) {
        setError(anyRes.error ?? 'Failed to release hold.');
        return;
      }
      void load();
    } catch (e: unknown) {
      if (ApiError.isApiError(e)) setError(e.message);
      else if (e instanceof Error) setError(e.message);
      else setError('Unexpected error releasing hold.');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
      <Banner tone="info">
        Retention holds keep a resolved cart restorable past its policy window. Use these for
        legal holds, regulator inquiries, or audit findings. Each release is logged so the
        next purge sweep treats the cart normally again.
      </Banner>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>
          {loading ? 'Loading…' : `${rows.length} active hold${rows.length === 1 ? '' : 's'}`}
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
          <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>⟳ Refresh</Button>
          <Button variant="primary" size="sm" onClick={() => setAddOpen(true)}>+ Add hold</Button>
        </div>
      </div>

      {error && <Banner tone="err">{error}</Banner>}

      <div style={{ background: 'var(--surface-card)', border: '1px solid var(--surface-border)', borderRadius: 'var(--r-md)', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--ink-100)', textAlign: 'left' }}>
              <th style={th()}>Cart (humanized)</th>
              <th style={th()}>Extended to</th>
              <th style={th()}>Placed at</th>
              <th style={th()}>Placed by</th>
              <th style={th()}>Reason</th>
              <th style={th()}>Action</th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: 'var(--sp-6)', textAlign: 'center', color: 'var(--ink-600)' }}>
                  No retention holds active right now.
                </td>
              </tr>
            )}
            {rows.map((h) => (
              <tr key={h.cartId} style={{ borderBottom: '1px solid var(--surface-border)' }}>
                <td style={td()}>
                  <Pill variant="muted">Cart</Pill> <span style={{ marginLeft: 6 }}>{humanizeCartId(h.cartId)}</span>
                </td>
                <td style={td({ whiteSpace: 'nowrap' })}>{formatDateTime(h.extendedTo)}</td>
                <td style={td({ whiteSpace: 'nowrap' })}>{formatDateTime(h.placedAt)}</td>
                <td style={td()}>{h.placedByDisplayName ?? h.placedByOfficerCode ?? '—'}</td>
                <td style={td({ maxWidth: 360 })} title={h.reason}>{h.reason}</td>
                <td style={td()}>
                  <Button variant="danger" size="sm" onClick={() => void release(h)}>
                    Release
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {addOpen && (
        <AddHoldDialog
          onClose={() => setAddOpen(false)}
          onAdded={() => {
            setAddOpen(false);
            void load();
          }}
        />
      )}
    </div>
  );
}

function AddHoldDialog({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }): JSX.Element {
  const { client, makeSignal } = useRecordsApi();
  const [cartId, setCartId] = useState<string>('');
  const [extendedTo, setExtendedTo] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const valid = cartId.trim().length > 0 && extendedTo && reason.trim().length >= 10;

  async function handleSubmit(): Promise<void> {
    if (!valid || submitting) return;
    const bypass = promptBypassKey();
    if (!bypass) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await client.retail.superAdmin.records.addRetentionHold(
        bypass,
        {
          cartId: cartId.trim(),
          extendedTo: new Date(extendedTo).toISOString() as Iso8601,
          reason: reason.trim(),
        },
        { signal: makeSignal() },
      );
      const anyRes = res as unknown as { success?: boolean; error?: string };
      if (anyRes.success === false) {
        setError(anyRes.error ?? 'Failed to add hold.');
        return;
      }
      onAdded();
    } catch (e: unknown) {
      if (ApiError.isApiError(e)) setError(e.message);
      else if (e instanceof Error) setError(e.message);
      else setError('Unexpected error adding hold.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell
      open
      size="md"
      title="Place retention hold"
      onClose={() => (submitting ? undefined : onClose())}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={() => void handleSubmit()} disabled={!valid || submitting}>
            {submitting ? 'Placing…' : 'Place hold'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--ink-500)' }}>
            Cart ID
          </span>
          <input
            aria-label="Cart ID"
            title="Cart ID"
            type="text"
            value={cartId}
            onChange={(e) => setCartId(e.target.value)}
            placeholder="Paste from records browser"
            style={inputStyle}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--ink-500)' }}>
            Extended to
          </span>
          <input
            aria-label="Hold expiration"
            title="Hold expiration"
            type="datetime-local"
            value={extendedTo}
            onChange={(e) => setExtendedTo(e.target.value)}
            style={inputStyle}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--ink-500)' }}>
            Reason (≥ 10 chars)
          </span>
          <textarea
            aria-label="Hold reason"
            title="Hold reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Legal hold reference, regulator inquiry, etc."
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'var(--font-sans)' }}
          />
        </label>
        {error && <Banner tone="err">{error}</Banner>}
      </div>
    </ModalShell>
  );
}

function humanizeCartId(cartId: string): string {
  // Records browser already humanizes — for this surface we just truncate
  // the GUID into a shorter ID-looking string. The full cartId is sent to
  // the backend on release; the user never has to memorize it.
  if (cartId.length <= 8) return cartId;
  return `${cartId.slice(0, 4)}…${cartId.slice(-4)}`;
}

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function th(): React.CSSProperties {
  return {
    padding: '10px 12px',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    color: 'var(--ink-600)',
    borderBottom: '1px solid var(--surface-border)',
    whiteSpace: 'nowrap',
  };
}

function td(extra?: React.CSSProperties): React.CSSProperties {
  return { padding: '10px 12px', verticalAlign: 'top', color: 'var(--ink-900)', ...extra };
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  fontSize: 13,
  border: '1px solid var(--ink-300)',
  borderRadius: 4,
  background: 'var(--surface-card)',
  width: '100%',
};
